import React, { useState } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const StepEmailContent = ({ campaignData, onDataChange, onSave, onMessage }) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Character limits
  const limits = {
    email_title: 50,
    email_subject: 100,
    email_body_template: 2000,
    cta_button_text: 30
  };

  // Validation functions
  const validateField = (field, value) => {
    const errors = { ...validationErrors };
    
    switch (field) {
      case 'email_title':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Email title is required';
        } else if (value.length > limits[field]) {
          errors[field] = `Title must be ${limits[field]} characters or less`;
        } else {
          delete errors[field];
        }
        break;
        
      case 'email_subject':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Email subject is required';
        } else if (value.length > limits[field]) {
          errors[field] = `Subject must be ${limits[field]} characters or less`;
        } else {
          delete errors[field];
        }
        break;
        
      case 'email_body_template':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Email content is required';
        } else if (value.length > limits[field]) {
          errors[field] = `Email content must be ${limits[field]} characters or less`;
        } else {
          delete errors[field];
        }
        break;
        
      case 'cta_button_text':
        if (!value || value.trim().length === 0) {
          errors[field] = 'Call to action text is required';
        } else if (value.length > limits[field]) {
          errors[field] = `CTA text must be ${limits[field]} characters or less`;
        } else {
          delete errors[field];
        }
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAllFields = () => {
    const title = campaignData.email_title || '';
    const subject = campaignData.email_subject || '';
    const body = campaignData.email_body_template || '';
    const cta = campaignData.cta_button_text || '';
    
    const titleValid = validateField('email_title', title);
    const subjectValid = validateField('email_subject', subject);
    const bodyValid = validateField('email_body_template', body);
    const ctaValid = validateField('cta_button_text', cta);
    
    return titleValid && subjectValid && bodyValid && ctaValid;
  };

  const handleFieldChange = (field, value) => {
    onDataChange(field, value);
    // Validate field on change
    setTimeout(() => validateField(field, value), 100);
  };

  const renderFieldError = (field) => {
    if (validationErrors[field]) {
      return (
        <div className="mt-1 flex items-center text-red-600 text-sm">
          <Icon name="AlertCircle" size={14} className="mr-1" />
          {validationErrors[field]}
        </div>
      );
    }
    return null;
  };

  const renderCharacterCount = (field, value) => {
    const count = value ? value.length : 0;
    const limit = limits[field];
    const isNearLimit = count > limit * 0.8;
    const isOverLimit = count > limit;
    
    return (
      <div className={`text-xs ${isOverLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-gray-500'}`}>
        {count}/{limit} characters
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Messaging</h3>
        <p className="text-gray-600">
          Customize the email that customers receive when requesting their review.
        </p>
      </div>

      {/* Subject Line */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900">Subject</h4>
          {renderCharacterCount('email_subject', campaignData.email_subject)}
        </div>
        <input
          type="text"
          value={campaignData.email_subject || 'How was your experience with {{business.name}}?'}
          onChange={(e) => handleFieldChange('email_subject', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
            validationErrors.email_subject ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="How was your experience with {{business.name}}?"
          maxLength={limits.email_subject}
        />
        {renderFieldError('email_subject')}
      </div>

      {/* Email Title */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900">Email Title</h4>
          {renderCharacterCount('email_title', campaignData.email_title)}
        </div>
        <input
          type="text"
          value={campaignData.email_title || 'How was our service?'}
          onChange={(e) => handleFieldChange('email_title', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
            validationErrors.email_title ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="How was our service?"
          maxLength={limits.email_title}
        />
        {renderFieldError('email_title')}
        <p className="mt-2 text-sm text-gray-500">
          This appears as the main headline in the email body.
        </p>
      </div>

      {/* Email Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900">Edit Email</h4>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
              <span className="text-sm text-gray-600">Active</span>
            </div>
          </div>
        </div>

        {/* Formatting Toolbar */}
        <div className="border border-gray-300 rounded-t-lg p-2 bg-gray-50">
          <div className="flex items-center space-x-1">
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Bold" size={16} />
            </button>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Italic" size={16} />
            </button>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Underline" size={16} />
            </button>
            <div className="w-px h-4 bg-gray-300 mx-1"></div>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Link" size={16} />
            </button>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Type" size={16} />
            </button>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Quote" size={16} />
            </button>
            <div className="w-px h-4 bg-gray-300 mx-1"></div>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="List" size={16} />
            </button>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="ListOrdered" size={16} />
            </button>
            <div className="w-px h-4 bg-gray-300 mx-1"></div>
            <button className="p-1 text-gray-600 hover:bg-gray-200 rounded">
              <Icon name="Paperclip" size={16} />
            </button>
          </div>
        </div>

        {/* Email Body Editor */}
        <div className="relative">
          <textarea
            value={campaignData.email_body_template || "Hi {{contact.name}},\n\nThank you for choosing {{business.name}} for your recent service! We hope everything went smoothly.\n\nYour feedback is incredibly valuable to us and helps other customers make informed decisions. Would you mind taking a moment to share your experience?\n\nIt only takes 2 minutes and would mean the world to us."}
            onChange={(e) => handleFieldChange('email_body_template', e.target.value)}
            rows={8}
            className={`w-full px-3 py-3 border-l border-r border-b rounded-b-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none ${
              validationErrors.email_body_template ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter your email content..."
            maxLength={limits.email_body_template}
          />
          <div className="absolute bottom-2 right-2 text-xs text-gray-400">
            {renderCharacterCount('email_body_template', campaignData.email_body_template)}
          </div>
        </div>
        {renderFieldError('email_body_template')}

        {/* Template Variables Info */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <div className="font-medium mb-1">Available Variables:</div>
              <div className="text-xs space-x-4">
                <span className="bg-blue-100 px-2 py-1 rounded">{'{{contact.name}}'}</span>
                <span className="bg-blue-100 px-2 py-1 rounded">{'{{contact.email}}'}</span>
                <span className="bg-blue-100 px-2 py-1 rounded">{'{{business.name}}'}</span>
                <span className="bg-blue-100 px-2 py-1 rounded">{'{{service.name}}'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900">Call to Action</h4>
          {renderCharacterCount('cta_button_text', campaignData.cta_button_text)}
        </div>
        <input
          type="text"
          value={campaignData.cta_button_text || 'Leave us a review'}
          onChange={(e) => handleFieldChange('cta_button_text', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent ${
            validationErrors.cta_button_text ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Leave us a review"
          maxLength={limits.cta_button_text}
        />
        {renderFieldError('cta_button_text')}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          variant="primary"
          disabled={isLoading || Object.keys(validationErrors).length > 0}
          onClick={async () => {
            // Validate all fields before saving
            if (!validateAllFields()) {
              if (onMessage) {
                onMessage('error', 'Please fix validation errors before saving.');
              }
              return;
            }

            setIsLoading(true);
            try {
              if (onSave) {
                // Pass step-specific data
                const stepData = {
                  email_title: campaignData.email_title,
                  email_subject: campaignData.email_subject,
                  email_body_template: campaignData.email_body_template,
                  cta_button_text: campaignData.cta_button_text
                };
                await onSave(stepData);
              }
              if (onMessage) {
                onMessage('success', 'Email settings saved successfully!');
              }
            } catch (error) {
              console.error('Save error:', error);
              if (onMessage) {
                onMessage('error', 'Failed to save settings. Please try again.');
              }
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </div>
          ) : (
            'Save Settings'
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepEmailContent; 